import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { api, ApiError } from "@/lib/api";
import { formatDateTime, formatDuration } from "@/lib/utils";

type Resp = {
  viewer: {
    id: string;
    issuanceId: string;
    name: string | null;
    email: string | null;
    company: string | null;
    firstSeenAt: string;
    lastSeenAt: string;
    ndaSignedAt: string | null;
    ndaSignatureName: string | null;
    accessTierReached: string;
  };
  interactions: Array<{
    videoId: string;
    title: string;
    category: string;
    rating: string | null;
    completionPercentage: number;
    watchDurationSeconds: number;
    rewatchCount: number;
    firstWatchedAt: string | null;
    lastWatchedAt: string | null;
  }>;
};

export function ViewerAnalyticsPage() {
  const { viewerId } = useParams<{ viewerId: string }>();
  const { data, isError, error } = useQuery({
    queryKey: ["viewer-analytics", viewerId],
    queryFn: () => api.get<Resp>(`/api/admin/analytics/viewer/${viewerId}`),
    enabled: !!viewerId,
    retry: false,
  });

  // Show why it failed instead of spinning on "Loading…" forever (QA bug 21).
  if (isError) {
    const msg = error instanceof ApiError && error.status === 403
      ? "You don't have access to this viewer's activity. It belongs to a pack outside your campaigns."
      : error instanceof Error ? error.message : "Couldn't load this viewer's activity.";
    return <p className="text-sm text-destructive">{msg}</p>;
  }
  if (!data) return <p className="text-sm text-muted-foreground">Loading…</p>;
  const v = data.viewer;

  return (
    <div className="space-y-4 max-w-5xl">
      <div className="flex items-center gap-3 flex-wrap">
        <h1 className="font-head text-2xl font-semibold">{v.name ?? v.email ?? "(anonymous viewer)"}</h1>
        <Badge variant="outline">tier: {v.accessTierReached.replace(/_/g, " ")}</Badge>
        {v.ndaSignedAt && <Badge variant="ok">NDA signed</Badge>}
        <Link to={`/admin/issuances/${v.issuanceId}`} className="ml-auto text-sm text-electric hover:underline">
          ← back to pack
        </Link>
      </div>
      <div className="text-sm text-muted-foreground">
        {v.email} {v.company ? `· ${v.company}` : ""}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader><CardTitle>Session</CardTitle></CardHeader>
          <CardContent>
            <dl className="text-sm grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
              <dt className="text-muted-foreground">First seen</dt><dd>{formatDateTime(v.firstSeenAt)}</dd>
              <dt className="text-muted-foreground">Last seen</dt><dd>{formatDateTime(v.lastSeenAt)}</dd>
              {v.ndaSignedAt && (
                <>
                  <dt className="text-muted-foreground">NDA signed</dt><dd>{formatDateTime(v.ndaSignedAt)}</dd>
                  <dt className="text-muted-foreground">Signed as</dt><dd>{v.ndaSignatureName ?? "—"}</dd>
                </>
              )}
            </dl>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle>Topic interest</CardTitle></CardHeader>
          <CardContent>
            <ul className="text-sm space-y-1">
              {data.interactions
                .filter((i) => i.rating)
                .map((i) => (
                  <li key={i.videoId} className="flex justify-between">
                    <span>{i.title}</span>
                    <Badge variant={i.rating === "very_interested" ? "ok" : i.rating === "interested" ? "default" : "muted"}>
                      {i.rating!.replace(/_/g, " ")}
                    </Badge>
                  </li>
                ))}
              {data.interactions.filter((i) => i.rating).length === 0 && (
                <li className="text-muted-foreground text-xs">Viewer didn't rate any topics</li>
              )}
            </ul>
          </CardContent>
        </Card>
      </div>
      <Card>
        <CardHeader><CardTitle>Watch history</CardTitle></CardHeader>
        <CardContent>
          <table className="w-full text-sm">
            <thead><tr className="border-b border-admin-rule text-left">
              <th className="p-2">Video</th><th className="p-2">Completion</th><th className="p-2">Watch time</th><th className="p-2">Rewatches</th><th className="p-2">Last watched</th>
            </tr></thead>
            <tbody>
              {data.interactions.map((i) => (
                <tr key={i.videoId} className="border-b border-admin-rule">
                  <td className="p-2">{i.title} <span className="text-xs text-muted-foreground ml-1">{i.category.replace(/_/g, " ")}</span></td>
                  <td className="p-2">{i.completionPercentage}%</td>
                  <td className="p-2">{formatDuration(i.watchDurationSeconds)}</td>
                  <td className="p-2">{i.rewatchCount}</td>
                  <td className="p-2 text-xs">{formatDateTime(i.lastWatchedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
